import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Grid } from '@nextiva/next-ui-components';
import { LocationDrawer } from '@nextiva/nextiva-location-js';
import { orderBy } from 'lodash';
import Fuse from 'fuse.js';
import { useQueryClient, useMutation } from 'react-query';

import {
  LocationList,
  SearchBar,
  SearchBarWrapper,
  PermissionNeeded,
} from 'components';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { defaultButtonStyle } from 'components/SearchBarWrapper';
import { LocationService, PhoneNumbersService } from 'services';
import {
  useGetLocations,
  useSnackbar,
  useGetAssignedLicensesList,
} from 'hooks';
import { logInToBroadSoft } from 'utils/logInToBroadSoft';
import { screens } from 'containers/pages/DataImport';

import DeleteLocationModal from './DeleteLocationModal';

const VOICE_ADMIN_ROLE_NAME = 'Voice Admin';
const VOICE_ADMIN_ROLE_TYPE = 'ROLE';
const SUPER_ADMIN_ROLE_TYPE = 'SUPER_ADMIN';

const importDropdownOptions = [{ importName: 'Bulk add new locations' }];

const locationButtonStyle = {
  ...defaultButtonStyle,
  width: '131px',
};

const validateRoleType = (roles) => {
  const role = roles.data.filter(
    (element) =>
      element.roleType === SUPER_ADMIN_ROLE_TYPE ||
      (element.roleType === VOICE_ADMIN_ROLE_TYPE &&
        element.name === VOICE_ADMIN_ROLE_NAME),
  );

  return role.length > 0;
};

const sortFunction = (item, sortFieldType, sortField) => {
  switch (sortFieldType) {
    case 'string':
      return item[sortField] ? item[sortField].toLowerCase() : '';
    default:
      return item[sortField];
  }
};

const sort = (arr, sortFieldType, sortField, sortDirection) => {
  return orderBy(
    arr,
    [(item) => sortFunction(item, sortFieldType, sortField)],
    [sortDirection],
  );
};

const filter = (list = [], searchTerm) => {
  const newList = list.map((item) => ({
    ...item,
    locationAddress: `${item.streetNumber || ''} ${item.streetName || ''} ${
      item.streetType || ''
    }, ${item.city} ${item.state || ''}, ${item.zip} ${item.country}`,
  }));

  if (searchTerm) {
    const options = {
      shouldSort: false,
      threshold: 0.25,
      keys: [
        'locationName',
        'locationAddress',
        'outgoingPhoneNumber',
        'callerId',
      ],
    };
    const fuse = new Fuse(newList, options);

    return fuse.search(searchTerm);
  }

  return newList;
};

export const Locations = ({ user, roles }) => {
  const {
    featureState: { bulkImportsLocations },
  } = useContext(FeatureContext);
  const queryClient = useQueryClient();

  const [hasLocationListError, setHasLocationListError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [callCenterLicense, setCallCenterLicense] = useState(false);
  const [locationDrawerStatus, setLocationDrawerStatus] = useState(false);
  const [locationToEdit, setLocationToEdit] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sorting, setSorting] = useState({
    type: 'string',
    field: 'locationName',
    order: 'asc',
  });
  const setSnackbar = useSnackbar();

  const isVoiceAccount = validateRoleType(roles);

  const locations = useGetLocations();
  const { data } = useGetAssignedLicensesList();

  useEffect(() => {
    if (data?.accountAvailableFeatures?.length) {
      const callCenterFeatures = [
        'Call Center - Basic',
        'Call Center - Standard',
        'Call Center - Premium',
      ];
      const callCenterCheck = callCenterFeatures.some((el) =>
        data?.accountAvailableFeatures?.includes(el),
      );
      setCallCenterLicense(callCenterCheck);
    }
  }, [data]);

  const hideEditLocationDrawer = () => setLocationDrawerStatus(false);

  const showEditLocationDrawer = () => setLocationDrawerStatus(true);

  const setBaseLocationList = (locs) =>
    queryClient.setQueryData('locations', locs);

  useEffect(() => {
    if (locations.isError) {
      setHasLocationListError(locations.error.status);
    }
  }, [locations.isError]);

  const createLocationToggle = () => {
    setLocationToEdit(undefined);
    showEditLocationDrawer();
  };

  const editLocationToggle = (location, isRequired) => {
    setLocationToEdit({ ...location, isRequired });
    showEditLocationDrawer();
  };

  const toggleDeleteModal = () => setIsModalOpen(!isModalOpen);

  const onDeleteLocation = (deleteLocation) =>
    new Promise((resolve, reject) =>
      deleteLocation(
        {},
        {
          onSuccess: () => {
            setSnackbar({ message: 'Location deleted.', severity: 'success' });
            resolve();
          },
          onError: () => {
            setSnackbar({
              message: 'An error occurred during deletion.',
              severity: 'error',
            });
            reject();
          },
          onSettled: toggleDeleteModal,
        },
      ),
    );

  const handleSort = (e) => {
    const { id } = e.currentTarget;
    const newSorting = {
      type: 'string',
      field: id,
      order: 'asc',
    };

    if (sorting.field === newSorting.field) {
      if (sorting.order === 'asc') {
        newSorting.order = 'desc';
      } else {
        newSorting.order = 'asc';
      }
    }

    if (id === 'userCount') {
      newSorting.type = 'number';
    }

    setSorting(newSorting);
  };

  const voiceSettingsClicked = (location) => {
    const { locationId, voiceEnabled } = location;
    const redirectUrl = `${window.location.origin}/apps/voice-admin/#/locations/${locationId}/settings/music-on-hold`;

    if (voiceEnabled) window.location.href = redirectUrl;
    else editLocationToggle(location, true);
  };

  const deleteLocationClicked = (location) => {
    setLocationToEdit(location);
    toggleDeleteModal();
  };

  const downloadLocationsCSV = () => {
    const csvDownloadLink = document.createElement('a');
    const csvHeader = [
      'locationName',
      'userCount',
      'outgoingPhoneNumber',
      'callerId',
    ].join(',');
    const csvBody = locations.data
      .map((loc) =>
        [
          loc.locationName,
          loc.userCount,
          loc.outgoingPhoneNumber,
          loc.callerId,
        ].join(','),
      )
      .join('\n');
    const csvData = `${csvHeader}\n${csvBody}`;
    const file = new Blob([csvData], { type: 'text/plain' });
    csvDownloadLink.href = URL.createObjectURL(file);
    csvDownloadLink.download = `locations.csv`;
    csvDownloadLink.click();
  };

  const { mutateAsync: getLocationAdmin } = useMutation(({ locationId }) =>
    LocationService.getLocationAdmin(locationId),
  );

  const callCenterClicked = ({ locationId }) => {
    getLocationAdmin({ locationId })
      .then((response) => {
        const {
          locationAdminId: userId,
          locationAdminPassword: password,
          locationAdminLoginUrl: url,
        } = response;

        logInToBroadSoft({ userId, password, url });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  };

  const setNotification = ({ message }) => {
    setSnackbar({ message, severity: 'success' });
  };

  const sortedLocations = sort(
    filter(locations.data, searchTerm),
    sorting.type,
    sorting.field,
    sorting.order,
  );

  if (hasLocationListError === 403) {
    return <PermissionNeeded />;
  }

  return (
    <Grid container justifyContent="center">
      <Grid container spacing={0}>
        <Grid item xs={12} className="m-locations">
          <div className="m-locations__action-bar">
            <div className="m-header-search-bar">
              {bulkImportsLocations ? (
                <SearchBarWrapper
                  id={screens.LOCATIONS_IMPORT}
                  label="Current Locations"
                  placeholder="Search locations"
                  searchData={setSearchTerm}
                  actionName="Add location"
                  onClick={createLocationToggle}
                  buttonStyle={locationButtonStyle}
                  importDropdownOptions={importDropdownOptions}
                  downloadCSV={downloadLocationsCSV}
                  data-testid="PlatformAdminUI-Locations-SearchBar"
                />
              ) : (
                <SearchBar
                  id="SearchBarManage"
                  label="Current Locations"
                  placeholder="Search locations"
                  searchData={setSearchTerm}
                  actionName="Create location"
                  onClick={createLocationToggle}
                  downloadCSV={downloadLocationsCSV}
                  data-testid="PlatformAdminUI-Locations-SearchBarManage"
                />
              )}
            </div>
          </div>
          <div className="m-table-header">
            <Grid container>
              <Grid item xs={2}>
                <div
                  id="locationName"
                  onClick={handleSort}
                  role="presentation"
                  data-testid="PlatformAdminUI-Locations-LocationName"
                  className="table-header-item"
                >
                  <span
                    className={clsx('Heading6', 'm-table-header__header__text')}
                    data-testid="PlatformAdminUI-Locations-Name"
                  >
                    Name
                  </span>
                  <span
                    className={clsx(
                      'm-table-header__header__col__text',
                      'sort_icon',
                      sorting.field === 'locationName' &&
                        `${sorting.order}_icon`,
                    )}
                    data-testid={`PlatformAdminUI-Locations-Name-${sorting.order}-icon`}
                  />
                </div>
              </Grid>
              <Grid item xs={4}>
                <div
                  id="locationAddress"
                  onClick={handleSort}
                  role="presentation"
                  data-testid="PlatformAdminUI-Locations-LocationName"
                  className="table-header-item"
                >
                  <span
                    className={clsx('Heading6', 'm-table-header__header__text')}
                    data-testid="PlatformAdminUI-Locations-Address"
                  >
                    Location Address
                  </span>
                  <span
                    className={clsx(
                      'm-table-header__header__col__text',
                      'sort_icon',
                      sorting.field === 'locationAddress' &&
                        `${sorting.order}_icon`,
                    )}
                    data-testid={`PlatformAdminUI-Locations-Name-${sorting.order}-icon`}
                  />
                </div>
              </Grid>
              <Grid item xs={1}>
                <div
                  id="userCount"
                  onClick={handleSort}
                  role="presentation"
                  data-testid="PlatformAdminUI-Locations-UserCount"
                  className="table-header-item"
                >
                  <span
                    className={clsx('Heading6', 'm-table-header__header__text')}
                    data-testid="PlatformAdminUI-Locations-Users"
                  >
                    Users
                  </span>
                  <span
                    className={clsx(
                      'm-table-header__header__col__text',
                      'sort_icon',
                      sorting.field === 'userCount' && `${sorting.order}_icon`,
                    )}
                    data-testid={`PlatformAdminUI-Locations-Users-${sorting.order}-icon`}
                  />
                </div>
              </Grid>
              <Grid item xs={2}>
                <div
                  id="outgoingPhoneNumber"
                  onClick={handleSort}
                  role="presentation"
                  data-testid="PlatformAdminUI-Locations-OutgoingPhoneNumber"
                  className="table-header-item"
                >
                  <span
                    className={clsx('Heading6', 'm-table-header__header__text')}
                    data-testid="PlatformAdminUI-Locations-OutgoingNumber"
                  >
                    Outgoing Number
                  </span>
                  <span
                    className={clsx(
                      'm-table-header__header__col__text',
                      'sort_icon',
                      sorting.field === 'outgoingPhoneNumber' &&
                        `${sorting.order}_icon`,
                    )}
                    data-testid={`PlatformAdminUI-Locations-OutgoingNumber-${sorting.order}-icon`}
                  />
                </div>
              </Grid>
              <Grid item xs={3}>
                <div
                  id="callerId"
                  onClick={handleSort}
                  role="presentation"
                  data-testid="PlatformAdminUI-Locations-Presentation"
                  className="table-header-item"
                >
                  <span
                    className={clsx('Heading6', 'm-table-header__header__text')}
                    data-testid="PlatformAdminUI-Locations-CallerID"
                  >
                    Caller ID
                  </span>
                  <span
                    className={clsx(
                      'm-table-header__header__col__text',
                      'sort_icon',
                      sorting.field === 'callerId' && `${sorting.order}_icon`,
                    )}
                    data-testid={`PlatformAdminUI-Locations-CallerID-${sorting.order}-icon`}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
          <div
            className="m-locations__list"
            data-testid="PlatformAdminUI-Locations-LocationTable"
          >
            <LocationList
              profile={user.data}
              voiceAction={voiceSettingsClicked}
              editAction={editLocationToggle}
              deleteAction={deleteLocationClicked}
              centerAction={callCenterClicked}
              locationsLoading={locations.isLoading}
              locations={sortedLocations}
              isVoiceAccount={isVoiceAccount}
              callCenterLicense={callCenterLicense}
            />
            {hasLocationListError && (
              <div className="m-locations__no-locations">
                No locations found.
              </div>
            )}
          </div>

          <LocationDrawer
            updateLocation={LocationService.updateLocation}
            createLocation={LocationService.createLocation}
            getStates={LocationService.getStates}
            getTimezones={LocationService.getTimezones}
            getLocationTypes={LocationService.getLocationTypes}
            getAvailableOutgoingPhoneNumbers={
              PhoneNumbersService.getAvailableOutgoingPhoneNumbers
            }
            getAvailablePhoneNumbers={
              PhoneNumbersService.getAvailablePhoneNumbers
            }
            hideEditLocationDrawer={hideEditLocationDrawer}
            setBaseLocationList={setBaseLocationList}
            setNotification={setNotification}
            selectedLocation={locationToEdit || {}}
            baseLocationList={locations.data || []}
            profile={user.data}
            open={locationDrawerStatus}
            onClose={hideEditLocationDrawer}
            allowOutgoingNumberUpdate
            setWizardActiveStep={voiceSettingsClicked}
          />
          <DeleteLocationModal
            toggleModal={toggleDeleteModal}
            isModalOpen={isModalOpen}
            location={locationToEdit}
            deleteAction={onDeleteLocation}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

Locations.propTypes = {
  roles: PropTypes.object,
  user: PropTypes.object,
};
Locations.defaultProps = {
  roles: null,
  user: null,
};

const mapStateToProps = (state) => ({
  roles: state['__global/roles'],
  user: state['__global/user'],
});

export default connect(mapStateToProps)(Locations);
